<script setup>
  import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/vue'
  import { watchThrottled } from '@vueuse/core'

  const { query } = useRoute()

  const { find } = useStrapi4()

  const employerTypes = reactive({
    ...(await useLazyAsyncData(
      'employerTypes',
      async () => {
        const res = await find('employer-types', {
          fields: ['name', 'slug'],
          pagination: { limit: 100, start: 0, withCount: false },
          sort: { name: 'asc' }
        })
        return res.data
      },
      { default: () => [] }
    ))
  })

  const regions = reactive({
    ...(await useLazyAsyncData(
      'regions',
      async () => {
        const res = await find('regions', {
          fields: ['name', 'slug'],
          pagination: { limit: 100, start: 0, withCount: false },
          sort: { name: 'asc' }
        })
        return res.data
      },
      {
        default: () => []
      }
    ))
  })

  const filters = reactive({
    region: query.localisation?.length ? regions.data.filter(({ slug }) => query.localisation.split(',').includes(slug)) : [],
    search: query.recherche?.length ? query.recherche : '',
    type: query.typeInstitution?.length ? employerTypes.data.filter(({ slug }) => query.typeInstitution.split(',').includes(slug)) : []
  })

  const total = ref(0)

  const employers = reactive({
    ...(await useLazyAsyncData(
      'employers',
      async () => {
        const res = await find('employers', {
          _q: filters.search.length ? filters.search.trim() : undefined,
          fields: ['name', 'slug'],
          filters: {
            id: { $notIn: employers.data.map(({ id }) => id) },
            locations: filters.region.length
              ? {
                  region: {
                    slug: { $in: filters.region.map(({ slug }) => slug) }
                  }
                }
              : undefined,
            type: filters.type.length
              ? {
                  slug: { $in: filters.type.map(x => x.slug) }
                }
              : undefined
          },
          pagination: { limit: 12, start: 0 },
          populate: {
            careerSite: { fields: ['active', 'slug'] },
            cover: { fields: ['url'] },
            logo: { fields: ['url'] },
            locations: {
              fields: ['*'],
              populate: {
                region: { fields: ['*'] }
              }
            }
          },
          sort: 'random'
        })
        if (res.meta.pagination.total > total.value) total.value = res.meta.pagination.total
        return [...employers.data, ...res.data]
      },
      {
        default: () => [],
        server: false
      }
    ))
  })

  watchThrottled(
    filters,
    async () => {
      const tmp = {}
      if (filters.region.length) tmp.localisation = filters.region.map(({ slug }) => slug).join(',')
      if (filters.search.length) tmp.recherche = filters.search
      if (filters.type.length) tmp.typeInstitution = filters.type.map(x => x.slug).join(',')
      await navigateTo({ query: tmp }, { replace: true })
      total.value = 0
      employers.data = []
      employers.refresh()
    },
    { throttle: 500 }
  )

  const onLoadMore = () => {
    if (employers.pending || employers.error) return
    if (employers.data.length < total.value) employers.refresh()
  }

  const { y } = useWindowScroll()
  const filtersElem = ref(null)
  const { width } = useWindowSize()
  const { height } = useElementSize(filtersElem)

  const isSearchBig = computed(() => {
    return width.value <= 768 || y.value < 170
  })
</script>

<template>
  <Page v-slot="{ page }" code="employers">
    <div v-if="!isSearchBig" :style="{ height: `${height}px` }"></div>
    <div ref="filtersElem" :class="['w-full', isSearchBig ? 'top-20 bg-zinc-100' : 'fixed top-16 z-10 bg-tarawera-900 shadow']">
      <div :class="['mx-auto px-4 transition-none ', isSearchBig ? 'max-w-screen-lg py-8 ' : 'max-w-screen-xl py-0']">
        <div :class="['mb-4 text-center', isSearchBig ? 'block' : 'hidden']">
          <h1 class="font-special text-3xl font-bold sm:text-4xl md:text-5xl">{{ page.title }}</h1>
          <div class="mt-4 text-xl sm:text-2xl">Entrez dans les coulisses des institutions</div>
        </div>
        <div :class="['rounded-xl bg-tarawera-900 text-white duration-300', isSearchBig ? 'p-8' : 'px-0 pb-4 pt-6']">
          <div class="-m-2 grid items-start sm:grid-cols-2 lg:grid-cols-3">
            <div class="relative m-2 sm:col-span-2 lg:col-span-1">
              <input class="form-input w-full" v-model="filters.search" placeholder="Recherche" type="search" />
            </div>

            <Listbox class="relative m-2 text-tarawera-900" v-if="employerTypes.data.length" v-model="filters.type" as="div" multiple>
              <ListboxButton class="form-select w-full" as="div">
                <div class="text-zinc-400" v-if="!filters.type.length">Type d’institution</div>
                <div class="-mx-1 -my-0.5 inline-flex flex-wrap" v-else>
                  <div
                    class="m-0.5 inline-flex cursor-pointer items-center space-x-1 whitespace-nowrap rounded-sm bg-viking-400 px-1 py-0.5 text-sm duration-300 hover:bg-viking-500"
                    v-for="item in filters.type"
                    :key="item.slug"
                    @click.stop="filters.type = filters.type.filter(x => x.slug !== item.slug)"
                  >
                    <span>{{ item.name }}</span>
                    <FontAwesomeIcon class="text-white" icon="fa-solid fa-xmark" size="sm" />
                  </div>
                </div>
              </ListboxButton>
              <ListboxOptions
                class="absolute z-10 mt-0.5 max-h-64 w-full max-w-full overflow-auto rounded border border-zinc-300 bg-white shadow focus:border-viking-400 focus:outline-none focus:ring-1 focus:ring-viking-400"
              >
                <ListboxOption
                  class="cursor-default truncate px-2 py-1 hover:bg-zinc-100"
                  v-for="item in employerTypes.data"
                  v-text="item.name"
                  :class="{
                    'text-zinc-400': filters.type.includes(item)
                  }"
                  :key="item.slug"
                  :value="item"
                />
              </ListboxOptions>
            </Listbox>

            <Listbox class="relative m-2 text-tarawera-900" v-if="regions.data.length" v-model="filters.region" as="div" multiple>
              <ListboxButton class="form-select w-full" as="div">
                <div class="text-zinc-400" v-if="!filters.region.length">Localisation</div>
                <div class="-mx-1 -my-0.5 inline-flex flex-wrap" v-else>
                  <div
                    class="m-0.5 inline-flex cursor-pointer items-center space-x-1 whitespace-nowrap rounded-sm bg-viking-400 px-1 py-0.5 text-sm duration-300 hover:bg-viking-500"
                    v-for="region in filters.region"
                    :key="region.slug"
                    @click.stop="filters.region = filters.region.filter(({ slug }) => slug !== region.slug)"
                  >
                    <span>{{ region.name }}</span>
                    <FontAwesomeIcon class="text-white" icon="fa-solid fa-xmark" size="sm" />
                  </div>
                </div>
              </ListboxButton>
              <ListboxOptions
                class="absolute z-10 mt-0.5 max-h-64 w-full max-w-full overflow-auto rounded border border-zinc-300 bg-white shadow focus:border-viking-400 focus:outline-none focus:ring-1 focus:ring-viking-400"
              >
                <ListboxOption
                  class="cursor-default truncate px-2 py-1 hover:bg-zinc-100"
                  v-for="region in regions.data"
                  v-text="region.name"
                  :class="{
                    'text-zinc-400': filters.region.includes(region)
                  }"
                  :key="region.id"
                  :value="region"
                />
              </ListboxOptions>
            </Listbox>
          </div>
        </div>
      </div>
    </div>

    <div class="mx-auto max-w-screen-xl px-4 py-16" :style="{ marginTop: isSearchBig ? '0px' : `calc(3rem + ${height}px)` }">
      <InfiniteScroll v-if="!employers.error && employers.data.length" @loadMore="onLoadMore">
        <div class="-m-4 flex flex-wrap items-center">
          <div class="w-full p-4 sm:w-1/2 lg:w-1/3" v-for="employer in employers.data" :key="employer.id">
            <CardEmployer :employer="employer" />
          </div>
        </div>
      </InfiniteScroll>
      <Loading class="mt-8" v-if="employers.pending" />
      <Error class="mt-8" v-else-if="employers.error" :retry="employers.refresh" />
    </div>
  </Page>
</template>
